import { LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'
import { HeadingNode } from '@lexical/rich-text'
import {
  $isElementNode,
  type DOMExportOutput,
  type DOMExportOutputMap,
  isHTMLElement,
  type Klass,
  type LexicalEditor,
  type LexicalNode,
  ParagraphNode,
  type TextFormatType,
  TextNode,
} from 'lexical'
import { type TextStyles } from '../emailTemplateTypes'
import { $getEmailNode, EmailNode } from '../nodes/EmailNode'
import { GroupNode } from '../nodes/GroupNode'
import { LockedBlockNode } from '../nodes/LockedBlockNode'
import { $findSlot, $isEmptySlot, $isSlotNode, SlotNode } from '../nodes/SlotNode'
import { $isVariableNode, VariableNode } from '../nodes/VariableNode'
import { CONTENT_MARKER } from '../nodes/wrapperStyle'

const MARKER_ATTR = 'data-lexical-content'
const SUBJECT_MARKER = '[[subject]]'

export const inertFragment = (html: string): DocumentFragment => {
  const template = new DOMParser().parseFromString('<template></template>', 'text/html').querySelector('template') as HTMLTemplateElement
  template.innerHTML = html
  return template.content
}

const fragmentFromHtml = (html: string): DocumentFragment => document.importNode(inertFragment(html), true)

const CONTENT_COMMENT = 'lexical-content'
const COMMENT_NODE = 8

const findComment = (node: Node, value: string): ChildNode | null =>
  Array.from(node.childNodes).reduce<ChildNode | null>(
    (found, child) => found ?? (child.nodeType === COMMENT_NODE && child.nodeValue === value ? child : findComment(child, value)),
    null,
  )

export const wrapperMarkup = (wrapper: string): string => wrapper.replace(CONTENT_MARKER, `<!--${CONTENT_COMMENT}-->`)

export const frameMarkup = (frame: string): string => frame.replace(CONTENT_MARKER, `<span ${MARKER_ATTR}></span>`)

export const wrapperHasContentSlot = (wrapper: string): boolean => findComment(inertFragment(wrapperMarkup(wrapper)), CONTENT_COMMENT) !== null

export const frameHasContentSlot = (frame: string): boolean =>
  new DOMParser().parseFromString(frameMarkup(frame), 'text/html').querySelector(`[${MARKER_ATTR}]`) !== null

const fillWrapper = (wrapper: string, content: Node[]): DocumentFragment => {
  const fragment = fragmentFromHtml(wrapperMarkup(wrapper))
  findComment(fragment, CONTENT_COMMENT)?.replaceWith(...content)
  return fragment
}

const styled = (tag: string, style: string | undefined): HTMLElement => {
  const element = document.createElement(tag)
  if (style) {
    element.setAttribute('style', style)
  }
  return element
}

const FORMAT_TAGS: Array<[TextFormatType, string]> = [
  ['bold', 'strong'],
  ['italic', 'em'],
  ['underline', 'u'],
  ['strikethrough', 's'],
]

const formatted = (node: TextNode, text: string): DOMExportOutput => ({
  element: [...FORMAT_TAGS].reverse().reduce<HTMLElement | Text>((inner, [format, tag]) => {
    if (!node.hasFormat(format)) {
      return inner
    }
    const wrapper = document.createElement(tag)
    wrapper.append(inner)
    return wrapper
  }, document.createTextNode(text)),
})

const $textStyles = (): TextStyles => $getEmailNode()?.getTextStyles() ?? {}

const $plainText = (slot: SlotNode): string =>
  slot
    .getAllTextNodes()
    .map((node) => ($isVariableNode(node) ? `{{ ${node.getVariable()} }}` : node.getTextContent()))
    .join('')

const $isSubjectSlot = (node: SlotNode): boolean => $getEmailNode()?.getFirstChild()?.getKey() === node.getKey()

export const $subjectText = (): string => {
  const subject = $getEmailNode()?.getFirstChild()
  return $isSlotNode(subject) ? $plainText(subject) : ''
}

const $isTrailingEmpty = (node: LexicalNode): boolean =>
  node.getTextContent().trim() === '' && node.getNextSiblings().every((sibling) => sibling.getTextContent().trim() === '') && node.getPreviousSibling() !== null

type Exporter = (editor: LexicalEditor, node: LexicalNode) => DOMExportOutput

const exportEmail: Exporter = (_editor, node) => {
  const email = node as EmailNode
  const parsed = new DOMParser().parseFromString(frameMarkup(email.getFrame()), 'text/html')
  const html = document.importNode(parsed.documentElement, true)
  const own = new Set(Array.from(html.childNodes))
  return {
    element: html,
    after: (generated) => {
      if (!isHTMLElement(generated)) {
        return undefined
      }
      const children = Array.from(generated.childNodes).filter((child) => !own.has(child))
      generated.querySelector(`[${MARKER_ATTR}]`)?.replaceWith(...children)
      const title = generated.querySelector('title')
      if (title?.textContent?.includes(SUBJECT_MARKER)) {
        title.textContent = title.textContent.replace(SUBJECT_MARKER, () => $subjectText())
      }
      return undefined
    },
  }
}

const exportParagraph: Exporter = (_editor, node) => {
  const slot = $findSlot(node)
  if (!slot) {
    return { element: document.createDocumentFragment() }
  }
  const { slotFormat, wrapper } = slot.getAttrs()
  if (slotFormat === 'rich' && $isTrailingEmpty(node)) {
    return { element: null }
  }
  const empty = $isElementNode(node) && node.getChildrenSize() === 0
  if (!wrapper) {
    if (slotFormat === 'plain') {
      return { element: document.createDocumentFragment() }
    }
    const paragraph = document.createElement('p')
    if (empty) {
      paragraph.append(document.createElement('br'))
    }
    return { element: paragraph }
  }
  return {
    element: document.createElement('span'),
    after: (generated) => {
      if (!isHTMLElement(generated)) {
        return undefined
      }
      const content: Node[] = Array.from(generated.childNodes)
      return fillWrapper(wrapper, empty && slotFormat === 'rich' ? [...content, document.createElement('br')] : content)
    },
  }
}

const exportList: Exporter = (_editor, node) => {
  const tag = (node as ListNode).getListType() === 'number' ? 'ol' : 'ul'
  return {
    element: styled(tag, $textStyles()[tag]),
    after: (generated) => {
      if (!isHTMLElement(generated)) {
        return undefined
      }
      Array.from(generated.children).forEach((item) => {
        const onlyList = item.children.length > 0 && Array.from(item.childNodes).every((child) => isHTMLElement(child) && /^(UL|OL)$/.test(child.tagName))
        const previous = item.previousElementSibling
        if (onlyList && previous) {
          previous.append(...Array.from(item.childNodes))
          item.remove()
        }
      })
      return undefined
    },
  }
}

const exportLink: Exporter = (_editor, node) => {
  const anchor = styled('a', $textStyles().a)
  anchor.setAttribute('href', (node as LinkNode).getURL())
  anchor.setAttribute('target', '_blank')
  anchor.setAttribute('rel', 'noopener noreferrer')
  return { element: anchor }
}

export const EMAIL_EXPORT: DOMExportOutputMap = new Map<Klass<LexicalNode>, Exporter>([
  [EmailNode, exportEmail],
  [
    SlotNode,
    (_editor, node) =>
      $isSubjectSlot(node as SlotNode) || $isEmptySlot(node as SlotNode) ? { element: null } : { element: document.createDocumentFragment() },
  ],
  [
    GroupNode,
    (_editor, node) => ({
      element: document.createElement('span'),
      after: (generated) => (isHTMLElement(generated) ? fillWrapper((node as GroupNode).getWrapper(), Array.from(generated.childNodes)) : undefined),
    }),
  ],
  [LockedBlockNode, (_editor, node) => ({ element: fragmentFromHtml((node as LockedBlockNode).getHtml()) })],
  [ParagraphNode, exportParagraph],
  [HeadingNode, () => ({ element: styled('h3', $textStyles().h3) })],
  [ListNode, exportList],
  [ListItemNode, () => ({ element: styled('li', $textStyles().li) })],
  [LinkNode, exportLink],
  [TextNode, (_editor, node) => formatted(node as TextNode, (node as TextNode).getTextContent())],
  [VariableNode, (_editor, node) => formatted(node as VariableNode, `{{ ${(node as VariableNode).getVariable()} }}`)],
])
